import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { CommunityProject, Project } from '@/types';
import { getCommunityProjects, publishProject } from '@/lib/supabase';
import { Download, Search, Loader2, Upload } from 'lucide-react';

interface CommunityProjectsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentProject: Project;
  onProjectSelect: (project: Project) => void;
}

export default function CommunityProjectsDialog({
  open,
  onOpenChange,
  currentProject,
  onProjectSelect,
}: CommunityProjectsDialogProps) {
  const [projects, setProjects] = useState<CommunityProject[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadProjects();
    }
  }, [open]);

  const loadProjects = async () => {
    try {
      const data = await getCommunityProjects();
      setProjects(data);
    } catch (error) {
      toast({
        title: 'Error Loading Projects',
        description: 'Failed to load community projects. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const validateProject = (project: any): project is Project => {
    return (
      project &&
      typeof project === 'object' &&
      'settings' in project &&
      'components' in project &&
      typeof project.settings === 'object' &&
      Array.isArray(project.components) &&
      project.settings.name &&
      project.settings.author &&
      project.settings.nickname
    );
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      // Validate project data
      if (!currentProject.settings.name || !currentProject.settings.author || !currentProject.settings.nickname) {
        throw new Error('Please fill in all project settings before publishing');
      }

      if (!currentProject.components.length) {
        throw new Error('Cannot publish an empty project');
      }

      await publishProject(currentProject);
      
      toast({
        title: 'Project Published Successfully',
        description: 'Your project has been shared with the community.',
      });
      
      await loadProjects();
    } catch (error: any) {
      console.error('Publishing error:', error);
      toast({
        title: 'Publishing Failed',
        description: error.message || 'There was an error publishing your project. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUploadJson = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const content = await file.text();
      const projectData = JSON.parse(content);

      if (!validateProject(projectData)) {
        throw new Error('Invalid project file format. Please ensure the JSON file contains valid project data.');
      }

      await publishProject(projectData);
      
      toast({
        title: 'Project Published Successfully',
        description: 'Your project JSON has been uploaded and shared with the community.',
      });
      
      await loadProjects();
    } catch (error: any) {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload project JSON. Please check the file format.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleUseProject = async (project: CommunityProject) => {
    try {
      onProjectSelect({
        components: project.components,
        settings: project.settings,
      });
      onOpenChange(false);
      toast({
        title: 'Project Loaded Successfully',
        description: `You are now using ${project.name} by ${project.author}`,
      });
    } catch (error) {
      toast({
        title: 'Error Loading Project',
        description: 'Failed to load the selected project. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.nickname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Community Projects</DialogTitle>
        </DialogHeader>
        
        <div className="flex justify-between items-center mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Button variant="outline" disabled={isUploading}>
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Upload Project JSON'}
              </Button>
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept=".json"
                onChange={handleUploadJson}
                disabled={isUploading}
              />
            </div>
            <Button onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                'Publish Current Project'
              )}
            </Button>
          </div>
        </div>

        <ScrollArea className="h-[600px] pr-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="border rounded-lg overflow-hidden bg-card"
              >
                <div className="aspect-video relative">
                  <img
                    src={project.preview_url}
                    alt={project.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&h=800&fit=crop';
                    }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg">{project.name}</h3>
                  <p className="text-sm text-muted-foreground">by {project.author}</p>
                  <p className="text-sm text-muted-foreground">{project.nickname}</p>
                  <div className="flex justify-between items-center mt-2">
                    <div className="text-sm text-muted-foreground">
                      <span>{project.downloads} downloads</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUseProject(project)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Use
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}